/* eslint-disable max-lines */
import React, { useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import QueryString from 'query-string';
import { Link } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import Text, { Inline } from 'components/base/Text';
import Table from 'components/base/Table';
import { getColor } from 'common/constants/_colors';
import { matchContributorMetricsToContributor } from 'common/services/contributors.service';
import AppRouter from 'common/AppRouter';
import { getContributorAvatarUrl } from 'common/constants/Avatars';
import { nullSafeGet } from 'common/utils/utils';
import { Flex } from '../base/layout-components';
import MultipleAvatars from '../MultipleAvatars';
import {
  TeamActivityLink,
  PlaceHolderCircle,
  PlaceHolderRow,
  NameColumnHeaderComponent,
  getContributerMetricsParams,
  getTopReviewer,
  sortContributorsMetrics
} from './ContributorsRosterByTeamHidePersonalInfo.service';
import {
  ContributorManagementMenuItems,
  MergedUserIcon
} from 'components/ContributorManagementMenu/ContributorManagementMenu';
import { SIZES } from 'common/constants/sizes';
import Burnout from './Burnout';
import {
  EMPTY_NUMBER,
  SEARCH_DATE_FORMAT,
  DAYS_PARAM
} from 'common/constants/constants';
import Wip from './Wip';
import TopReviewer from './TopReviewer';
import {
  StyledWidgetHeader,
  tableNoBorderStyle
} from 'components/base/common-styles';
import { SkeletonRow } from '../Skeleton';
import { metricNames } from '@linearb/linjs-common';

export const LinkWithoutUnderline = styled(Link)`
  text-decoration: none;
  color: ${getColor('black')};
`;

const BlueTag = styled.div`
  background-color: ${getColor('dodgerBlue')};
  color: ${getColor('white')};
  padding: 0.2rem 0.4rem;
  border-radius: 0.2rem;
  margin-left: 0.5rem;
  cursor: default;
  font-weight: bold;
  width: fit-content;
`;

const StyledList = styled.div`
  padding: 0.4rem;
  display: flex;
  flex-direction: column;
  height: calc(100% - 0.8rem);
  ${tableNoBorderStyle};
`;

const TitleBox = styled(Text)`
  padding: 2rem;
`;

const StyledStatus = styled.div`
  min-width: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;

  & .status-text {
    margin-right: 1rem;
  }
`;

const YouTag = () => <BlueTag>YOU</BlueTag>;

const getData = (
  contributors,
  metrics,
  currentIteration,
  isRetrospect,
  teams,
  myAccount,
  isFinishedOnBoarding
) => {
  const contributorsIds = _.keyBy(contributors, 'id');

  const myProviderId = nullSafeGet(
    myAccount,
    'settings.provider_id',
    EMPTY_NUMBER
  ).toString();

  if (!isFinishedOnBoarding && !metrics.length) {
    // on onboarding - before we have team metrics:
    const teamContributorsIds = _.get(teams, '[0].contributor_ids', []);
    return contributors.length
      ? contributors
          .filter(({ id }) => teamContributorsIds.includes(id))
          .map(({ email, id, name, username, extended_data }) => ({
            name: (
              <Flex items='center'>
                <MultipleAvatars
                  disableTooltip={!email}
                  avatars={[
                    {
                      name: email,
                      url: getContributorAvatarUrl({ id, extended_data })
                    }
                  ]}
                />
                <Flex pl='small' direction='column' items='left'>
                  <Text
                    pl={SIZES.small}
                    cursor='pointer'
                    size='large'
                    weight={400}
                    ellipsisAfter='13rem'
                  >
                    {name ? name : username}
                  </Text>
                </Flex>
                {extended_data.provider_id === myProviderId && <YouTag />}
              </Flex>
            )
          }))
      : null;
  }
  let metricsWithEmpty;

  if (!isFinishedOnBoarding) {
    const teamContributorsIds = [..._.get(teams, '[0].contributor_ids', [])];
    metrics.forEach(({ contributor_id }) =>
      _.remove(teamContributorsIds, id => id === contributor_id)
    );
    const emptyContributors = teamContributorsIds.map(id => ({
      contributor_id: id,
      hasNoData: true
    }));
    metricsWithEmpty = [...metrics, ...emptyContributors];
  }

  const iterationDays =
    moment(moment(isRetrospect ? currentIteration.end_date : undefined)).diff(
      moment(currentIteration.start_date),
      DAYS_PARAM
    ) + 1;

  const topReviewer = getTopReviewer({ contributors, metrics });

  const contributorsMetrics = matchContributorMetricsToContributor(
    contributors,
    isFinishedOnBoarding ? metrics : metricsWithEmpty
  );

  const sortedContributorsMetrics = sortContributorsMetrics(
    contributorsMetrics,
    topReviewer,
    iterationDays
  );

  return sortedContributorsMetrics.map(contributorMetric => {
    const {
      contributor_id: contributorId,
      name,
      username,
      avatarUrl,
      providerId,
      hasNoData,
      team = {}
    } = contributorMetric;

    const { is_parent: isParent = false, contained_ids: childIds = [] } =
      contributorsIds[contributorId];

    const contribToLink = {
      pathname: AppRouter.BRANCH.path,
      search: QueryString.stringify({
        metric_name: metricNames.ACTIVE_BRANCHES,
        contributors: contributorId,
        time_range: [
          moment(currentIteration.start_date).format(SEARCH_DATE_FORMAT),
          moment
            .min(moment(currentIteration.end_date), moment())
            .format(SEARCH_DATE_FORMAT)
        ].join(',')
      })
    };

    const { reviews, activeCount, activityDays } = getContributerMetricsParams({
      contributorMetric
    });

    const isBurnout =
      activityDays > 6 && activityDays / iterationDays >= 0.9 && !hasNoData;

    const isWipOverload = activeCount >= 6 && !hasNoData;

    const isTopReviewer =
      topReviewer.contributorId === contributorId && reviews > 0 && !hasNoData;

    const styles = team.hasLeftOnly ? { grayedOut: true } : {};

    const firstName = name ? name.split(' ')[0] : username;

    const tooltipTitle = `View ${firstName}’s activity`;

    const nameCol = (
      <Flex
        items={team.info ? 'baseline' : 'center'}
        direction='row'
        className='items-center gap-1'
      >
        <Link
          style={{ textDecoration: 'none', color: getColor('black') }}
          to={contribToLink}
        >
          <Tooltip title={tooltipTitle}>
            <Flex items='center' direction='row' width='100%'>
              <MultipleAvatars
                changeCursor
                avatars={[{ url: avatarUrl }]}
                disableTooltip
              />
              <Flex pl='small' direction='column' items='left'>
                <Flex>
                  <Inline size='large' weight={400}>
                    <Text cursor='pointer' size='large' weight={400}>
                      {name ? name : username}
                    </Text>
                  </Inline>
                  {providerId === myProviderId && <YouTag />}
                </Flex>
                {team.info && (
                  <Inline size='small' weight={400} color='gray'>
                    {team.info}
                  </Inline>
                )}
              </Flex>
            </Flex>
          </Tooltip>
        </Link>
        {isParent && <MergedUserIcon childIds={childIds} />}
      </Flex>
    );

    const AchievementsCol = (
      <Flex items='center' direction='row' width='100%' justify='flex-end'>
        <Flex items='center' justify='center' gap='0.7rem'>
          {isBurnout && (
            <StyledStatus>
              <Burnout
                name={firstName}
                isWipOverload={isWipOverload}
                isTopReviewer={isTopReviewer}
              />
            </StyledStatus>
          )}
          {isWipOverload && (
            <StyledStatus>
              <Wip
                isTopReviewer={isTopReviewer}
                isBurnout={isBurnout}
                isRetrospect={isRetrospect}
                name={firstName}
                link={{
                  pathname: AppRouter.BRANCH.path,
                  search: QueryString.stringify({
                    metric_name: metricNames.ACTIVE_BRANCHES,
                    contributors: contributorId,
                    time_range: [
                      moment(currentIteration.start_date).format(
                        SEARCH_DATE_FORMAT
                      ),
                      moment
                        .min(moment(currentIteration.end_date), moment())
                        .format(SEARCH_DATE_FORMAT)
                    ].join(',')
                  })
                }}
              />
            </StyledStatus>
          )}
          {isTopReviewer && (
            <StyledStatus>
              <TopReviewer
                name={name}
                reviews={reviews}
                isBurnout={isBurnout}
                isWipOverload={isWipOverload}
              />
            </StyledStatus>
          )}
        </Flex>
      </Flex>
    );

    return {
      styles,
      name: nameCol,
      menu: (
        <ContributorManagementMenuItems
          currentContributor={contributorMetric}
        />
      ),
      Achievements: hasNoData ? (
        <Flex items='center' gap='2rem'>
          <PlaceHolderRow />
          <PlaceHolderCircle size='2' />
        </Flex>
      ) : (
        AchievementsCol
      )
    };
  });
};

const ContributorsRosterByTeamHidePersonalInfo = props => {
  const {
    contributors,
    currentIteration,
    isRetrospect = false,
    contributorsMetrics,
    order,
    onOrderChange,
    selectedTeam,
    teams,
    myAccount,
    isFinishedOnBoarding
  } = props;

  const getColumns = ({ currentIteration }) => [
    {
      dataPoint: 'name',
      ColumnHeaderComponent: () => (
        <NameColumnHeaderComponent contributorsMetrics={contributorsMetrics} />
      )
    },
    {
      dataPoint: 'Achievements',
      tableCellProps: {
        style: {
          maxWidth: '12rem'
        }
      }
    },
    {
      dataPoint: 'menu',
      name: '',
      ColumnHeaderComponent: () => (
        <TeamActivityLink
          selectedTeam={selectedTeam}
          currentIteration={currentIteration}
        />
      ),
      showOnlyOnHover: true,
      tableCellProps: {
        style: {
          width: '2.2rem',
          paddingLeft: 0
        }
      }
    }
  ];

  const nonEmptyContributorsMetrics =
    contributorsMetrics.length && contributorsMetrics;

  const data = useMemo(
    () =>
      getData(
        contributors,
        contributorsMetrics,
        currentIteration,
        isRetrospect,
        teams,
        myAccount,
        isFinishedOnBoarding
      ),
    [
      contributors,
      nonEmptyContributorsMetrics,
      currentIteration,
      isRetrospect,
      teams,
      myAccount
    ]
  );
  const columns = getColumns({
    teamId: selectedTeam.id,
    currentIteration,
    isRetrospect
  });

  if (!contributors.length || !data.length) {
    const emptyBadgeLocation = [1, 3];
    return (
      <Flex direction='column' justify='center'>
        <TitleBox>
          <StyledWidgetHeader>Team Members</StyledWidgetHeader>
        </TitleBox>
        {[...Array(5).keys()].map((item, index) => {
          return (
            <Flex items='center' gap='1rem' padding='small' key={index}>
              <Flex
                width={emptyBadgeLocation.includes(index) ? '100%' : '50%'}
                items='center'
                gap='1rem'
              >
                <PlaceHolderCircle size='2' />
                <SkeletonRow width={'100%'} />
              </Flex>
              {emptyBadgeLocation.includes(index) && (
                <Flex width='100%' justify='flex-end' items='center'>
                  <SkeletonRow width='40%' />
                  <PlaceHolderCircle size='1' />
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>
    );
  }

  return (
    <StyledList>
      <Table
        tableComponentProps={{ stickyHeader: true }}
        sortBy={order.by}
        sortOrder={order.dir}
        onSort={(colName, order) => onOrderChange({ by: colName, dir: order })}
        columns={columns}
        data={data}
        lineHighlightOnHover={true}
      ></Table>
    </StyledList>
  );
};

export default ContributorsRosterByTeamHidePersonalInfo;
